// skills/fishing/fishingUI.js
// Fishing UI - Gatherer-style card layout

import { fishingSkill } from './fishing.js';
import { FISH, RODS } from './fishingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatWithCommas } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentLootBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

/**
 * Render compact header with rod upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.FISHING);
    const xpPerHour = getXPPerHour(SKILLS.FISHING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.FISHING);

    // Tool info
    const activeTool = fishingSkill.getActiveBuff();
    const rods = Object.values(RODS);
    const currentIndex = rods.findIndex(r => r.id === activeTool.id);
    const nextRod = rods[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextRod) {
        const canAfford = playerGold >= nextRod.price;
        if (skill.level < nextRod.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextRod.name} (${nextRod.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextRod.price}">${getItemIcon('coins')} ${formatWithCommas(nextRod.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextRod.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.fishingUI.upgradeRod('${nextRod.id}')">
                    → ${nextRod.name} (${nextRod.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextRod.price}">${getItemIcon('coins')} ${formatWithCommas(nextRod.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.FISHING)}</span>
                    <span class="compact-name">Fishing</span>
                    ${renderSkillInfo(SKILLS.FISHING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.FISHING)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Rod:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render a single fish card
 */
function renderFishCard(fish, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= fish.level;
    const isActive = currentAction?.type === 'fishing' && currentAction?.fish?.id === fish.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = fishingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.FISHING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.FISHING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = fish.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.FISHING);
    let effectiveXP = fish.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities (talent + corruption)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.FISHING);
    const talentLootBonus = getTalentLootBonus(SKILLS.FISHING);

    // Build drop display (fish have single drops)
    const drop = fish.drops[0];
    const itemName = getItemName(drop.itemId);
    const itemIcon = getItemIcon(drop.itemId);
    const baseQty = drop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="drop-qty boosted">x${effectiveQty}</span>`
        : `<span class="drop-qty">x${baseQty}</span>`;

    const cardClasses = [
        'fish-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-fish-id="${fish.id}"
             style="--fish-color: ${fish.color}">

            <div class="fish-card-header">
                <span class="fish-card-level">${isUnlocked ? `Lvl ${fish.level}` : `Req: ${fish.level}`}</span>
                <div class="fish-icon-wrapper">${getItemIcon(fish.drops[0].itemId)}</div>
                <span class="fish-card-name">${fish.name}</span>
            </div>

            <div class="fish-card-info">
                <div class="fish-stat">
                    <span class="stat-icon fishing-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="fish-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="fish-drops">
                <div class="drop-info fish-drop">
                    <span class="drop-name">${itemIcon} ${itemName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="fish-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.fishingUI.toggleFishing('${fish.id}')">
                        ${isActive ? 'Stop' : 'Fish'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="fish-progress">
                <div class="fish-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render the fishing interface
 */
export function renderFishingUI() {
    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.FISHING);
    const currentAction = gameState.getCurrentAction();

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.FISHING);

    const fishHtml = Object.values(FISH)
        .map(fish => renderFishCard(fish, skill.level, currentAction))
        .join('');

    container.innerHTML = `
        <div class="fishing-interface">
            ${renderCompactHeader()}
            <div class="fish-grid">
                ${fishHtml}
            </div>
        </div>
    `;
}

/**
 * Toggle fishing on/off for a fish
 */
export function toggleFishing(fishId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.fish?.id === fishId) {
        fishingSkill.stopFishing();
    } else {
        const result = fishingSkill.canFish(fishId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            fishingSkill.startFishing(fishId);
        }
    }

    renderFishingUI();
}

/**
 * Upgrade rod
 */
export function upgradeRod(rodId) {
    const result = fishingSkill.upgradeRod(rodId);

    if (result.success) {
        renderFishingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Update fishing UI each tick
 */
export function updateFishingUI() {
    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.FISHING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderFishingUI();
        }
        return;
    }

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.FISHING);
    const xpPerHour = getXPPerHour(SKILLS.FISHING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.FISHING);

    const xpTotalEl = document.querySelector('.fishing-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.fishing-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.fishing-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    // Update progress bar using helper
    updateCompactHeaderProgress('.fishing-interface', SKILLS.FISHING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.fishing-interface');

    // Update action progress if fishing is active
    const action = gameState.getCurrentAction();
    if (!action || action.type !== 'fishing') return;

    const activeCard = document.querySelector(`.fish-card[data-fish-id="${action.fish.id}"]`);
    if (activeCard) {
        const cardProgressFill = activeCard.querySelector('.fish-progress-fill');
        if (cardProgressFill) {
            cardProgressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

/**
 * Setup fishing skill listeners
 */
export function initFishingUI() {
    fishingSkill.initializeStartingTool();
    fishingSkill.onFishingComplete = (fish, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Fishing level ${xpResult.newLevel}!`);
        }

        // Only re-render if fishing page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.FISHING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderFishingUI();
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.fishingUI = {
        toggleFishing,
        upgradeRod
    };
}

export default {
    renderFishingUI,
    toggleFishing,
    upgradeRod,
    updateFishingUI,
    initFishingUI
};
